import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SectionHeading from "@/components/SectionHeading";
import CountUp from "@/components/CountUp";
import { CITY_PAGES } from "@/lib/site-data";

const ZONE_STATS = [
  { value: `${CITY_PAGES.length}+`, label: "communes desservies" },
  { value: "2 h", label: "délai d'intervention" },
  { value: "10 ans", label: "de garantie décennale" },
];

const ease = [0.22, 1, 0.36, 1];

const ZONE_POINTS = [
  { name: "Bordeaux Centre", coords: [44.8378, -0.5792] },
  { name: "Chartrons", coords: [44.8546, -0.5716] },
  { name: "Caudéran", coords: [44.8508, -0.6137] },
  { name: "Bastide", coords: [44.8367, -0.5513] },
  { name: "Saint-Michel", coords: [44.8296, -0.5664] },
  { name: "Mérignac", coords: [44.8386, -0.6451] },
  { name: "Pessac", coords: [44.8067, -0.6310] },
  { name: "Talence", coords: [44.8048, -0.5854] },
  { name: "Bègles", coords: [44.8086, -0.5478] },
  { name: "Villenave-d’Ornon", coords: [44.7733, -0.5644] },
  { name: "Gradignan", coords: [44.7740, -0.6150] },
  { name: "Libourne", coords: [44.9150, -0.2430] },
  { name: "Saint-Émilion", coords: [44.8940, -0.1550] },
  { name: "Bassin d’Arcachon", coords: [44.662, -1.166] },
  { name: "Cenon", coords: [44.858, -0.531] },
  { name: "Floirac", coords: [44.838, -0.525] },
  { name: "Lormont", coords: [44.876, -0.521] },
  { name: "Bouliac", coords: [44.815, -0.501] },
  { name: "Artigues-près-Bordeaux", coords: [44.859, -0.483] },
  { name: "Ambarès-et-Lagrave", coords: [44.926, -0.487] },
  { name: "Carbon-Blanc", coords: [44.896, -0.507] },
  { name: "Bassens", coords: [44.903, -0.515] },
  { name: "Sainte-Eulalie", coords: [44.908, -0.474] },
  { name: "Yvrac", coords: [44.881, -0.461] },
  { name: "Tresses", coords: [44.848, -0.463] },
  { name: "Carignan-de-Bordeaux", coords: [44.826, -0.466] },
  { name: "Montussan", coords: [44.882, -0.428] },
  { name: "Ambès", coords: [45.012, -0.533] },
  { name: "Saint-André-de-Cubzac", coords: [45.004, -0.445] },
  { name: "Eysines", coords: [44.883, -0.652] },
  { name: "Blanquefort", coords: [44.911, -0.638] },
  { name: "Le Bouscat", coords: [44.864, -0.6] },
  { name: "Bruges", coords: [44.886, -0.612] },
  { name: "Parempuyre", coords: [44.949, -0.605] },
  { name: "Saint-Médard-en-Jalles", coords: [44.896, -0.714] },
  { name: "Saint-Aubin-de-Médoc", coords: [44.913, -0.723] },
  { name: "Martignas-sur-Jalle", coords: [44.844, -0.766] },
  { name: "Le Haillan", coords: [44.872, -0.68] },
  { name: "Le Taillan-Médoc", coords: [44.904, -0.67] },
  { name: "Cestas", coords: [44.744, -0.681] },
  { name: "Saint-Jean-d'Illac", coords: [44.81, -0.789] },
  { name: "Léognan", coords: [44.728, -0.6] },
  { name: "Martillac", coords: [44.713, -0.542] },
  { name: "Latresne", coords: [44.784, -0.496] },
  { name: "Cadaujac", coords: [44.754, -0.53] },
  { name: "Canéjan", coords: [44.763, -0.654] },
  { name: "Fronsac", coords: [44.919, -0.274] },
  { name: "Castillon-la-Bataille", coords: [44.856, -0.041] },
  { name: "Arcachon", coords: [44.659, -1.165] },
  { name: "La Teste-de-Buch", coords: [44.633, -1.146] },
  { name: "Gujan-Mestras", coords: [44.638, -1.069] },
  { name: "Lanton", coords: [44.704, -1.037] },
  { name: "Latès de Buch", coords: [44.615, -1.08] },
  { name: "Andernos-les-Bains", coords: [44.743, -1.098] },
  { name: "Arès", coords: [44.765, -1.138] },
  { name: "Lège-Cap-Ferret", coords: [44.8, -1.15] },
  { name: "Audenge", coords: [44.687, -0.989] },
  { name: "Biganos", coords: [44.646, -0.979] },
  { name: "Le Teich", coords: [44.635, -1.023] },
  { name: "Mios", coords: [44.604, -0.936] },
];

const makeIcon = (active) =>
  L.divIcon({
    className: "tbm-pin",
    html: `<span class="pin${active ? " pin-active" : ""}"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

function FlyTo({ zone }) {
  const map = useMap();
  useEffect(() => {
    if (zone) map.flyTo(zone.coords, 12, { duration: 1.1 });
  }, [zone, map]);
  return null;
}

export default function Zones() {
  const [active, setActive] = useState(null);
  const activeZone = ZONE_POINTS.find((z) => z.name === active) || null;

  return (
    <section id="zones" data-testid="zones-section" className="relative border-t border-white/5 bg-[#0A0C0F] py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Zones d’intervention"
          title="Bordeaux, sa métropole et la Gironde"
          copy="Basés à Mérignac, aux portes de Bordeaux, nous intervenons dans toute la métropole, le Libournais et jusqu’au Bassin d’Arcachon. Touchez un point ou une ville pour localiser le secteur."
        />
        <div className="mt-14 grid gap-8 lg:grid-cols-[1fr_1.5fr]">
          <div>
            <div className="flex flex-wrap gap-3">
              {ZONE_POINTS.map((z, i) => (
                <motion.button
                  key={z.name}
                  data-testid={`zone-chip-${z.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.04, duration: 0.5, ease }}
                  onClick={() => setActive(z.name)}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${
                    active === z.name
                      ? "border-terra bg-terra text-white"
                      : "border-white/10 text-slate-300 hover:border-terra/50 hover:text-white"
                  }`}
                >
                  <MapPin className={`h-3.5 w-3.5 ${active === z.name ? "text-white" : "text-terra"}`} />
                  {z.name}
                </motion.button>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease }}
              className="mt-10 flex flex-wrap items-center gap-4 border border-terra/20 bg-terra/5 p-6"
            >
              <Clock className="h-6 w-6 shrink-0 text-terra" />
              <p className="text-sm text-slate-300 md:text-base">
                <span className="font-semibold text-white">Intervention sous 2 h</span> pour les urgences fuite dans
                toute la Gironde — 7 jours sur 7, jours fériés compris.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease }}
            className="overflow-hidden border border-white/5"
          >
            <div data-testid="zones-map" className="h-[420px] w-full md:h-[540px]">
              <MapContainer
                center={[44.8, -0.62]}
                zoom={10}
                scrollWheelZoom={false}
                className="h-full w-full"
                style={{ background: "#0A0C0F" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FlyTo zone={activeZone} />
                {ZONE_POINTS.map((z) => (
                  <Marker
                    key={z.name}
                    position={z.coords}
                    icon={makeIcon(active === z.name)}
                    eventHandlers={{ click: () => setActive(z.name) }}
                  >
                    <Popup>
                      <strong>{z.name}</strong>
                      <br />
                      Intervention sous 2 h
                      <br />
                      Devis gratuit
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          data-testid="zones-stats"
          className="mt-14 grid grid-cols-3 gap-6 border-t border-white/10 pt-10"
        >
          {ZONE_STATS.map((s) => (
            <div key={s.label} data-testid={`zones-stat-${s.label.replace(/\s/g, "-")}`} className="text-center md:text-left">
              <p className="font-serif text-3xl font-bold text-white md:text-5xl">
                <CountUp value={s.value} />
              </p>
              <p className="mt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300 md:text-xs">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>

        <div className="mt-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Nos pages locales</p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2.5">
            {CITY_PAGES.map((c) => (
              <Link
                key={c.slug}
                to={`/couvreur/${c.slug}`}
                data-testid={`zone-link-${c.slug}`}
                className="text-xs text-slate-400 underline-offset-4 transition-colors hover:text-terra hover:underline"
              >
                Couvreur à {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
